// ============================================
// 文件: src/pages/Reports.jsx
// 举报管理页面
// ============================================
import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Row,
  Col,
  Statistic,
  Descriptions,
  Image,
  InputNumber,
  Radio,
  Divider,
  Popconfirm,
  Typography
} from 'antd';
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { getReportList, getReportDetail, handleReport, getReportStats } from '../api/reports';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Option } = Select;
const { Text, Paragraph } = Typography;

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [searchForm] = Form.useForm();
  const [detailVisible, setDetailVisible] = useState(false);
  const [handleVisible, setHandleVisible] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);
  const [handleForm] = Form.useForm();
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    resolved: 0,
    rejected: 0
  });

  // 举报类型映射
  const reportTypeMap = {
    spam: { text: '垃圾信息', color: 'default' },
    adult: { text: '色情内容', color: 'red' },
    violence: { text: '暴力内容', color: 'orange' },
    fraud: { text: '欺诈信息', color: 'purple' },
    other: { text: '其他', color: 'blue' }
  };

  // 状态映射
  const statusMap = {
    pending: { text: '待处理', color: 'gold' },
    processing: { text: '处理中', color: 'blue' },
    resolved: { text: '已处理', color: 'green' },
    rejected: { text: '已驳回', color: 'red' }
  };

  // 加载统计数据
  const loadStats = async () => {
    try {
      const res = await getReportStats();
      if (res.success) {
        setStats(res.data.overview);
      }
    } catch (error) {
      console.error('加载统计失败:', error);
    }
  };

  // 加载列表
  const loadData = async (params = {}) => {
    setLoading(true);
    try {
      const searchValues = searchForm.getFieldsValue();
      const dateRange = searchValues.dateRange;
      
      const requestParams = {
        page: pagination.current,
        pageSize: pagination.pageSize,
        ...params,
        status: searchValues.status,
        report_type: searchValues.report_type,
        keyword: searchValues.keyword,
        startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
        endDate: dateRange?.[1]?.format('YYYY-MM-DD')
      };

      const res = await getReportList(requestParams);
      if (res.success) {
        setDataSource(res.data.list);
        setPagination({
          ...pagination,
          total: res.data.pagination.total,
          current: res.data.pagination.page
        });
      }
    } catch (error) {
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    loadStats();
  }, []);

  // 搜索
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    loadData({ page: 1 });
  };

  // 重置
  const handleReset = () => {
    searchForm.resetFields();
    setPagination({ ...pagination, current: 1 });
    loadData({ page: 1 });
  };

  // 表格改变
  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
    loadData({
      page: newPagination.current,
      pageSize: newPagination.pageSize
    });
  };

  // 查看详情
  const showDetail = async (record) => {
    try {
      const res = await getReportDetail(record.id);
      if (res.success) {
        setCurrentReport(res.data);
        setDetailVisible(true);
      }
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  // 显示处理对话框
  const showHandle = (record) => {
    setCurrentReport(record);
    handleForm.resetFields();
    setHandleVisible(true);
  };

  // 提交处理
  const handleSubmit = async () => {
    try {
      const values = await handleForm.validateFields();
      
      await handleReport(currentReport.id, values);
      
      message.success('处理成功');
      setHandleVisible(false);
      loadData();
      loadStats();
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      message.error('处理失败');
    }
  };

  // 快速操作
  const quickHandle = async (record, action) => {
    try {
      let data = { action };
      
      if (action === 'approve') {
        data.handle_result = '举报成立，已删除违规内容';
      } else if (action === 'reject') {
        data.handle_result = '举报不成立';
      }
      
      await handleReport(record.id, data);
      message.success('处理成功');
      loadData();
      loadStats();
    } catch (error) {
      message.error('处理失败');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
      fixed: 'left'
    },
    {
      title: '举报类型',
      dataIndex: 'report_type',
      width: 120,
      render: (type) => {
        const config = reportTypeMap[type] || reportTypeMap.other;
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '被举报帖子',
      dataIndex: 'post_title',
      width: 200,
      ellipsis: true,
      render: (text, record) => (
        <Space direction="vertical" size={4}>
          <Text ellipsis style={{ width: 180 }}>
            {text || '(无标题)'}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            作者: {record.post_user_nickname}
          </Text>
        </Space>
      )
    },
    {
      title: '举报人',
      dataIndex: 'reporter_nickname',
      width: 120
    },
    {
      title: '举报原因',
      dataIndex: 'reason',
      width: 200,
      ellipsis: true
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status) => {
        const config = statusMap[status] || statusMap.pending;
        return <Tag color={config.color}>{config.text}</Tag>;
      },
      filters: [
        { text: '待处理', value: 'pending' },
        { text: '处理中', value: 'processing' },
        { text: '已处理', value: 'resolved' },
        { text: '已驳回', value: 'rejected' }
      ]
    },
    {
      title: '举报时间',
      dataIndex: 'created_at',
      width: 160,
      render: (time) => dayjs(time).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '处理时间',
      dataIndex: 'handled_at',
      width: 160,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '处理人',
      dataIndex: 'handler_nickname',
      width: 120,
      render: (text) => text || '-'
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 240,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => showDetail(record)}
          >
            详情
          </Button>
          {record.status === 'pending' && (
            <>
              <Button
                type="link"
                size="small"
                onClick={() => showHandle(record)}
              >
                处理
              </Button>
              <Popconfirm
                title="确认删除该帖子？"
                onConfirm={() => quickHandle(record, 'approve')}
              >
                <Button type="link" size="small" danger>
                  删帖
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总举报数"
              value={stats.total}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="待处理"
              value={stats.pending}
              valueStyle={{ color: '#faad14' }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已处理"
              value={stats.resolved}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已驳回"
              value={stats.rejected}
              valueStyle={{ color: '#ff4d4f' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 搜索表单 */}
      <Card style={{ marginBottom: 16 }}>
        <Form form={searchForm} layout="inline">
          <Form.Item name="keyword">
            <Input placeholder="搜索举报人/帖子标题" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="状态" allowClear style={{ width: 120 }}>
              <Option value="pending">待处理</Option>
              <Option value="processing">处理中</Option>
              <Option value="resolved">已处理</Option>
              <Option value="rejected">已驳回</Option>
            </Select>
          </Form.Item>
          <Form.Item name="report_type">
            <Select placeholder="举报类型" allowClear style={{ width: 120 }}>
              <Option value="spam">垃圾信息</Option>
              <Option value="adult">色情内容</Option>
              <Option value="violence">暴力内容</Option>
              <Option value="fraud">欺诈信息</Option>
              <Option value="other">其他</Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange">
            <RangePicker />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" onClick={handleSearch}>
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1600 }}
        />
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title="举报详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={900}
      >
        {currentReport && (
          <div>
            <Descriptions title="举报信息" bordered column={2}>
              <Descriptions.Item label="举报ID">
                {currentReport.id}
              </Descriptions.Item>
              <Descriptions.Item label="举报类型">
                <Tag color={reportTypeMap[currentReport.report_type]?.color}>
                  {reportTypeMap[currentReport.report_type]?.text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="举报人">
                {currentReport.reporter_nickname}
              </Descriptions.Item>
              <Descriptions.Item label="举报时间">
                {dayjs(currentReport.created_at).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="状态" span={2}>
                <Tag color={statusMap[currentReport.status]?.color}>
                  {statusMap[currentReport.status]?.text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="举报原因" span={2}>
                <Paragraph>{currentReport.reason || '无'}</Paragraph>
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Descriptions title="被举报帖子" bordered column={2}>
              <Descriptions.Item label="帖子标题" span={2}>
                {currentReport.post_title}
              </Descriptions.Item>
              <Descriptions.Item label="发布者">
                {currentReport.post_user_nickname}
              </Descriptions.Item>
              <Descriptions.Item label="违规次数">
                {currentReport.post_user_violation_count}
              </Descriptions.Item>
              <Descriptions.Item label="帖子状态">
                <Tag>{currentReport.post_status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="点赞数">
                {currentReport.post_like_count}
              </Descriptions.Item>
              <Descriptions.Item label="帖子描述" span={2}>
                <Paragraph>{currentReport.post_description}</Paragraph>
              </Descriptions.Item>
              <Descriptions.Item label="帖子图片" span={2}>
                <Image.PreviewGroup>
                  {currentReport.post_photos?.map((photo, index) => (
                    <Image
                      key={index}
                      width={100}
                      src={photo.photo_url}
                      style={{ marginRight: 8 }}
                    />
                  ))}
                </Image.PreviewGroup>
              </Descriptions.Item>
            </Descriptions>

            {currentReport.status === 'resolved' && (
              <>
                <Divider />
                <Descriptions title="处理信息" bordered column={2}>
                  <Descriptions.Item label="处理人">
                    {currentReport.handler_nickname}
                  </Descriptions.Item>
                  <Descriptions.Item label="处理时间">
                    {dayjs(currentReport.handled_at).format('YYYY-MM-DD HH:mm:ss')}
                  </Descriptions.Item>
                  <Descriptions.Item label="处理结果" span={2}>
                    {currentReport.handle_result}
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}

            {currentReport.other_reports && currentReport.other_reports.length > 0 && (
              <>
                <Divider />
                <div>
                  <Text strong>该帖子的其他举报记录 ({currentReport.other_reports.length})</Text>
                  <Table
                    dataSource={currentReport.other_reports}
                    columns={[
                      { title: '举报人', dataIndex: 'reporter_nickname', width: 120 },
                      { 
                        title: '类型', 
                        dataIndex: 'report_type',
                        width: 100,
                        render: (type) => reportTypeMap[type]?.text
                      },
                      { 
                        title: '状态', 
                        dataIndex: 'status',
                        width: 100,
                        render: (status) => (
                          <Tag color={statusMap[status]?.color}>
                            {statusMap[status]?.text}
                          </Tag>
                        )
                      },
                      { 
                        title: '时间', 
                        dataIndex: 'created_at',
                        render: (time) => dayjs(time).format('MM-DD HH:mm')
                      }
                    ]}
                    pagination={false}
                    size="small"
                    style={{ marginTop: 12 }}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* 处理弹窗 */}
      <Modal
        title="处理举报"
        open={handleVisible}
        onOk={handleSubmit}
        onCancel={() => setHandleVisible(false)}
        width={600}
      >
        {currentReport && (
          <div>
            <Descriptions bordered column={1} size="small" style={{ marginBottom: 24 }}>
              <Descriptions.Item label="举报类型">
                <Tag color={reportTypeMap[currentReport.report_type]?.color}>
                  {reportTypeMap[currentReport.report_type]?.text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="被举报帖子">
                {currentReport.post_title}
              </Descriptions.Item>
              <Descriptions.Item label="举报原因">
                {currentReport.reason}
              </Descriptions.Item>
            </Descriptions>

            <Form form={handleForm} layout="vertical">
              <Form.Item
                name="action"
                label="处理方式"
                rules={[{ required: true, message: '请选择处理方式' }]}
              >
                <Radio.Group>
                  <Radio value="approve">仅删除帖子</Radio>
                  <Radio value="ban_user">仅封禁用户</Radio>
                  <Radio value="approve_and_ban">删帖 + 封号</Radio>
                  <Radio value="reject">驳回举报</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => 
                  prevValues.action !== currentValues.action
                }
              >
                {({ getFieldValue }) => {
                  const action = getFieldValue('action');
                  if (action === 'ban_user' || action === 'approve_and_ban') {
                    return (
                      <>
                        <Form.Item
                          name="ban_days"
                          label="封禁天数"
                          rules={[{ required: true, message: '请输入封禁天数' }]}
                          initialValue={7}
                        >
                          <InputNumber
                            min={1}
                            max={365}
                            style={{ width: '100%' }}
                            placeholder="输入封禁天数，0表示永久封禁"
                          />
                        </Form.Item>
                        <Form.Item
                          name="ban_reason"
                          label="封禁原因"
                        >
                          <TextArea rows={3} placeholder="填写封禁原因" />
                        </Form.Item>
                      </>
                    );
                  }
                  return null;
                }}
              </Form.Item>

              <Form.Item
                name="handle_result"
                label="处理说明"
                rules={[{ required: true, message: '请填写处理说明' }]}
              >
                <TextArea rows={4} placeholder="填写处理说明，将记录到系统日志" />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Reports;
